import { useCallback, useRef, useState, type FC } from "react";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import type { DateRange } from "@/stores/filterStore";
import {
  countDaysInclusive,
  formatDateRangeLabel,
  getMinSelectableDate,
  getYesterday,
  parseISODate,
  QUICK_SELECT_RESOLVERS,
  toISODate,
  type DraftRange,
  type QuickSelectKey,
} from "@/layouts/AppLayout/dateRangePresets";
import { TEXT } from "@/constants/text";
import styles from "@/layouts/AppLayout/DateRangePicker.module.css";

// Two visually separated groups, matching the mockup's gap: rolling windows
// anchored to yesterday, then calendar-aligned (month/quarter) ranges.
const ROLLING_QUICK_SELECTS: { key: QuickSelectKey; label: string }[] = [
  { key: "yesterday", label: TEXT.dateRangePicker.yesterday },
  { key: "lastSevenDays", label: TEXT.dateRangePicker.lastSevenDays },
  { key: "lastFourteenDays", label: TEXT.dateRangePicker.lastFourteenDays },
  { key: "lastThirtyDays", label: TEXT.dateRangePicker.lastThirtyDays },
  { key: "lastNinetyDays", label: TEXT.dateRangePicker.lastNinetyDays },
];

const CALENDAR_ALIGNED_QUICK_SELECTS: { key: QuickSelectKey; label: string }[] =
  [
    { key: "thisMonth", label: TEXT.dateRangePicker.thisMonth },
    { key: "lastMonth", label: TEXT.dateRangePicker.lastMonth },
    { key: "thisQuarter", label: TEXT.dateRangePicker.thisQuarter },
    { key: "lastQuarter", label: TEXT.dateRangePicker.lastQuarter },
  ];

interface QuickSelectButtonProps {
  label: string;
  isActive: boolean;
  onClick: () => void;
}

const QuickSelectButton: FC<QuickSelectButtonProps> = ({
  label,
  isActive,
  onClick,
}) => (
  <button
    aria-pressed={isActive}
    className={
      isActive ? `${styles.quickSelect} ${styles.active}` : styles.quickSelect
    }
    type="button"
    onClick={onClick}
  >
    {label}
  </button>
);

const rangeCalendarPt = {
  panel: { className: styles.rangePanel },
};

// PrimeReact's own CalendarDateTemplateEvent isn't exported from its .d.ts —
// narrowed here to just the fields this template reads (mirrors the
// FilterTemplateOptions/PanelHeaderTemplateOptions pattern in
// hooks/useFilterReset.tsx for the same reason).
interface DateTemplateEvent {
  day: number;
  month: number;
  year: number;
}

// PrimeReact's Calendar fires local-midnight Date objects from its onChange —
// normalize to UTC noon using local getters so toISODate produces the correct
// calendar day for UTC+ users (a local midnight at e.g. UTC+5:30 is already
// the previous UTC day, which .toISOString().slice(0,10) would return instead).
const normalizeCalendarDate = (date: Date): Date =>
  new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 12));

// Mirrors PrimeReact's own isDateEquals (local getters, not UTC) so this
// lines up with whichever day it already decided to mark .p-highlight.
const isSameGridDate = (date: Date | null, event: DateTemplateEvent): boolean =>
  date !== null &&
  date.getDate() === event.day &&
  date.getMonth() === event.month &&
  date.getFullYear() === event.year;

// The PRD (mockup 5.4) specifies these two fields as native date inputs, not
// a second calendar widget — the two-month grid below is the one picker
// surface; a PrimeReact Calendar here would pop its own overlay on top of it.
const toDateInputValue = (date: Date | null): string =>
  date ? toISODate(date) : "";

const fromDateInputValue = (value: string): Date | null =>
  value ? parseISODate(value) : null;

// The store's DateRange (ISO strings) seeds this component's own Date-based
// draft state — converted once here, at the store boundary, so everything
// downstream (the Calendar, quick-selects, date-math) works in Date, and
// FilterStore$ itself never has to hold a Date instance.
const toDraftRange = (range: DateRange): DraftRange => ({
  start: range.start ? parseISODate(range.start) : null,
  end: range.end ? parseISODate(range.end) : null,
});

interface DateRangePickerProps {
  initialRange: DateRange;
  onApply: (range: { start: Date; end: Date }) => void;
  onCancel: () => void;
}

const DateRangePicker: FC<DateRangePickerProps> = ({
  initialRange,
  onApply,
  onCancel,
}) => {
  const [now] = useState(() => new Date());
  const yesterday = getYesterday(now);
  const minDate = getMinSelectableDate(now);

  const [draftRange, setDraftRange] = useState<DraftRange>(() =>
    toDraftRange(initialRange)
  );
  const [activeQuickSelect, setActiveQuickSelect] =
    useState<QuickSelectKey | null>(null);

  const startInputRef = useRef<HTMLInputElement>(null);
  const endInputRef = useRef<HTMLInputElement>(null);

  const onQuickSelectClick = (key: QuickSelectKey) => {
    setDraftRange(QUICK_SELECT_RESOLVERS[key](now));
    setActiveQuickSelect(key);
  };

  const onRangeCalendarChange = (value: (Date | null)[] | null) => {
    const [start, end] = value ?? [null, null];
    setDraftRange({
      start: start ? normalizeCalendarDate(start) : null,
      end: end ? normalizeCalendarDate(end) : null,
    });
    setActiveQuickSelect(null);
  };

  const onStartDateChange = (value: Date | null) => {
    setDraftRange((current) => ({ ...current, start: value }));
    setActiveQuickSelect(null);
  };

  const onEndDateChange = (value: Date | null) => {
    setDraftRange((current) => ({ ...current, end: value }));
    setActiveQuickSelect(null);
  };

  const canApply =
    Boolean(draftRange.start && draftRange.end) &&
    // Prevent inverted ranges that could slip through via keyboard entry on
    // browsers that ignore <input type="date"> min/max enforcement (e.g.
    // Firefox text-mode) — normalise rather than reject, since the calendar
    // grid can't produce them.
    draftRange.start!.getTime() <= (draftRange.end?.getTime() ?? 0) &&
    // Enforce T-1 upper bound: today has no data yet, so a range ending today
    // or in the future must not apply — the Calendar maxDate already blocks
    // this in the picker, but text inputs don't enforce it on all browsers.
    draftRange.end!.getTime() <= yesterday.getTime() &&
    // Enforce the rolling 1-year lower bound for the same reason — text
    // inputs don't enforce min on all browsers.
    draftRange.start!.getTime() >= minDate.getTime();

  // True when a quick-select like "This Quarter" is chosen on period day 1:
  // the natural period start is after yesterday (no data exists yet), so the
  // resolver returns start > end. canApply already blocks Apply; this flag
  // prevents the inverted dates from reaching the Calendar and <input> elements
  // where they would trigger :invalid browser styling or undefined range rendering.
  const isInverted =
    Boolean(draftRange.start && draftRange.end) &&
    draftRange.start!.getTime() > draftRange.end!.getTime();

  const dateTemplate = useCallback(
    (event: DateTemplateEvent) =>
      isSameGridDate(draftRange.start, event) ||
      isSameGridDate(draftRange.end, event) ? (
        <span className={styles.rangeEndpointDay}>{event.day}</span>
      ) : (
        event.day
      ),
    [draftRange.start, draftRange.end]
  );

  const onApplyClick = () => {
    if (!draftRange.start || !draftRange.end) return;
    onApply({ start: draftRange.start, end: draftRange.end });
  };

  const rangeLabel = canApply ? formatDateRangeLabel(draftRange) : "";
  const summaryMeta = canApply
    ? [
        TEXT.dateRangePicker.daysCountLabel.replace(
          "{0}",
          String(countDaysInclusive(draftRange))
        ),
        TEXT.dateRangePicker.dataThroughYesterday,
      ].join(" · ")
    : TEXT.dateRangePicker.dataThroughYesterday;

  return (
    <div className={styles.picker}>
      <div className={styles.fields}>
        <div className={styles.field}>
          <span className={styles.fieldLabel}>
            {TEXT.dateRangePicker.startDateLabel}
          </span>
          <div className={styles.fieldInputWrapper}>
            <button
              aria-label={TEXT.dateRangePicker.startDateLabel}
              className={styles.fieldIcon}
              type="button"
              onClick={() => startInputRef.current?.showPicker?.()}
            >
              <i aria-hidden="true" className="pi pi-calendar" />
            </button>
            <input
              ref={startInputRef}
              className={styles.fieldInput}
              max={toDateInputValue(
                isInverted ? yesterday : (draftRange.end ?? yesterday)
              )}
              min={toDateInputValue(minDate)}
              type="date"
              value={toDateInputValue(isInverted ? null : draftRange.start)}
              onChange={(e) =>
                onStartDateChange(fromDateInputValue(e.target.value))
              }
            />
          </div>
        </div>
        <i aria-hidden="true" className="pi pi-arrow-right" />
        <div className={styles.field}>
          <span className={styles.fieldLabel}>
            {TEXT.dateRangePicker.endDateLabel}
          </span>
          <div className={styles.fieldInputWrapper}>
            <button
              aria-label={TEXT.dateRangePicker.endDateLabel}
              className={styles.fieldIcon}
              type="button"
              onClick={() => endInputRef.current?.showPicker?.()}
            >
              <i aria-hidden="true" className="pi pi-calendar" />
            </button>
            <input
              ref={endInputRef}
              className={styles.fieldInput}
              max={toDateInputValue(yesterday)}
              min={toDateInputValue(
                !isInverted && draftRange.start ? draftRange.start : minDate
              )}
              type="date"
              value={toDateInputValue(isInverted ? null : draftRange.end)}
              onChange={(e) =>
                onEndDateChange(fromDateInputValue(e.target.value))
              }
            />
          </div>
        </div>
      </div>

      <div className={styles.body}>
        <div className={styles.quickSelects}>
          <span className={styles.quickSelectLabel}>
            {TEXT.dateRangePicker.quickSelectLabel}
          </span>
          {ROLLING_QUICK_SELECTS.map(({ key, label }) => (
            <QuickSelectButton
              key={key}
              isActive={key === activeQuickSelect}
              label={label}
              onClick={() => onQuickSelectClick(key)}
            />
          ))}
          <div className={styles.calendarAlignedGroup}>
            {CALENDAR_ALIGNED_QUICK_SELECTS.map(({ key, label }) => (
              <QuickSelectButton
                key={key}
                isActive={key === activeQuickSelect}
                label={label}
                onClick={() => onQuickSelectClick(key)}
              />
            ))}
          </div>
        </div>
        <div className={styles.rangeCalendar}>
          <Calendar
            dateTemplate={dateTemplate}
            inline
            maxDate={yesterday}
            minDate={minDate}
            numberOfMonths={2}
            pt={rangeCalendarPt}
            selectionMode="range"
            value={
              isInverted ? [null, null] : [draftRange.start, draftRange.end]
            }
            onChange={(e) =>
              onRangeCalendarChange((e.value as (Date | null)[] | null) ?? null)
            }
          />
          <p className={styles.freshnessNote}>
            {TEXT.dateRangePicker.selectableWindowNote}
          </p>
        </div>
      </div>

      <div className={styles.footer}>
        <span className={styles.summary}>
          {rangeLabel && (
            <span className={styles.summaryRange}>{rangeLabel} · </span>
          )}
          {summaryMeta}
        </span>
        <div className={styles.footerButtons}>
          <Button
            label={TEXT.dateRangePicker.cancelButton}
            outlined
            pt={{ root: { className: styles.cancelButton } }}
            onClick={onCancel}
          />
          <Button
            disabled={!canApply}
            icon="pi pi-arrow-right"
            iconPos="right"
            label={TEXT.dateRangePicker.applyButton}
            pt={{ root: { className: styles.applyButton } }}
            onClick={onApplyClick}
          />
        </div>
      </div>
    </div>
  );
};

export default DateRangePicker;
